import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Members Library — gated reports listing (v8.6: list-only view).
 *
 * On mount, GETs /api/members-fetch-report (no slug = manifest). The
 * function checks the session cookie server-side; on 401, we bounce
 * to /members.
 *
 * Each report renders as a full-width card. Clicking a card navigates
 * to /members/library/:slug, where a dedicated full-screen viewer
 * renders the report HTML — important for usability on phones, where
 * a sidebar+iframe layout squeezed the report to a tiny right column.
 */
export default function MembersLibrary() {
  const [state, setState] = useState({ status: 'loading' });

  useEffect(() => {
    window.scrollTo(0, 0);
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/members-fetch-report', {
          credentials: 'same-origin',
        });
        if (cancelled) return;
        if (res.status === 401) {
          window.location.replace('/members?error=session_required');
          return;
        }
        if (!res.ok) {
          setState({ status: 'error', message: `Server error (${res.status})` });
          return;
        }
        const data = await res.json();
        const reports = Array.isArray(data.reports) ? data.reports : [];
        // Newest first by publishedAt (fallback: manifest order).
        reports.sort((a, b) =>
          (b.publishedAt || '').localeCompare(a.publishedAt || '')
        );
        setState({ status: 'ready', email: data.email, reports });
      } catch (err) {
        if (!cancelled) setState({ status: 'error', message: err.message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === 'loading') {
    return (
      <div style={loadingWrapStyle}>
        <p style={loadingTextStyle}>Loading your library…</p>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div style={loadingWrapStyle}>
        <p style={{ ...loadingTextStyle, color: '#ffb8b8' }}>
          {state.message || 'Something went wrong.'}
        </p>
        <Link
          to="/members"
          style={{ color: '#4a9eff', marginTop: '1rem', textDecoration: 'none' }}
        >
          Back to sign in
        </Link>
      </div>
    );
  }

  const { email, reports } = state;

  return (
    <div style={pageStyle}>
      <div style={containerStyle}>
        {/* Header */}
        <div style={headerWrapStyle}>
          <div style={kickerStyle}>Members Library</div>
          <h1 style={headingStyle}>Reports archive</h1>
          <p style={subheadingStyle}>
            Signed in as <strong style={{ color: '#ffffff' }}>{email}</strong>
          </p>
        </div>

        {/* Empty state */}
        {reports.length === 0 ? (
          <div style={emptyStyle}>
            <p>No reports have been published yet. Check back Monday.</p>
          </div>
        ) : (
          <>
            <div style={countLabelStyle}>
              {reports.length} {reports.length === 1 ? 'report' : 'reports'}
            </div>
            <div style={listStyle}>
              {reports.map((r) => (
                <ReportCard key={r.slug} report={r} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------- Report card ---------- */

function ReportCard({ report }) {
  const [hover, setHover] = useState(false);
  return (
    <Link
      to={`/members/library/${encodeURIComponent(report.slug)}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        ...cardStyle,
        background: hover
          ? 'rgba(74, 158, 255, 0.10)'
          : 'rgba(255, 255, 255, 0.03)',
        borderColor: hover
          ? 'rgba(74, 158, 255, 0.45)'
          : 'rgba(74, 158, 255, 0.15)',
        transform: hover ? 'translateY(-1px)' : 'translateY(0)',
      }}
    >
      <div style={cardLeftStyle}>
        <div style={cardDateStyle}>{report.publishedAt}</div>
        <div style={cardTitleStyle}>{report.title}</div>
      </div>
      <div style={cardArrowStyle} aria-hidden="true">
        →
      </div>
    </Link>
  );
}

/* ---------- styles ---------- */

const pageStyle = {
  minHeight: 'calc(100vh - 80px)',
  padding: '3rem 1.25rem',
};
const containerStyle = {
  maxWidth: '900px',
  margin: '0 auto',
};
const headerWrapStyle = { marginBottom: '2.5rem' };
const kickerStyle = {
  fontSize: '0.85rem',
  color: '#4a9eff',
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
  fontWeight: 600,
};
const headingStyle = {
  fontSize: '2.25rem',
  fontWeight: 700,
  color: '#ffffff',
  marginBottom: '0.5rem',
};
const subheadingStyle = {
  color: '#a8c5e6',
  fontSize: '0.95rem',
  // Long emails wrap naturally on narrow screens.
  wordBreak: 'break-word',
};
const emptyStyle = {
  padding: '3rem 1.5rem',
  background: 'rgba(255, 255, 255, 0.03)',
  border: '1px solid rgba(74, 158, 255, 0.15)',
  borderRadius: '12px',
  textAlign: 'center',
  color: '#a8c5e6',
};
const countLabelStyle = {
  fontSize: '0.75rem',
  fontWeight: 600,
  color: '#6b9bc9',
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  marginBottom: '0.75rem',
  padding: '0 0.25rem',
};
const listStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
};
const cardStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '1rem',
  padding: '1.1rem 1.25rem',
  border: '1px solid rgba(74, 158, 255, 0.15)',
  borderRadius: '12px',
  textDecoration: 'none',
  color: 'inherit',
  transition: 'background 0.15s, border-color 0.15s, transform 0.15s',
  cursor: 'pointer',
};
const cardLeftStyle = {
  flex: 1,
  minWidth: 0, // allows the title to truncate/wrap cleanly inside a flex parent
};
const cardDateStyle = {
  fontSize: '0.72rem',
  color: '#4a9eff',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  fontWeight: 600,
  marginBottom: '0.3rem',
};
const cardTitleStyle = {
  fontSize: '1rem',
  fontWeight: 600,
  color: '#ffffff',
  lineHeight: 1.35,
};
const cardArrowStyle = {
  fontSize: '1.4rem',
  color: '#6b9bc9',
  flexShrink: 0,
};
const loadingWrapStyle = {
  minHeight: 'calc(100vh - 80px)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem',
};
const loadingTextStyle = {
  color: '#a8c5e6',
  fontSize: '1rem',
};
